import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  extractColumnsFromPdf,
  generateAndTestParser,
  testParserCode,
  createStatementParser,
  updateStatementParser,
  getStatementParser,
  getStatementParsers,
  deleteStatementParser,
} from '../services/api';
import './AdminParserBuilder.css';

type MappedTo = 'date' | 'particulars' | 'debit' | 'credit' | 'balance' | 'chequeRefNumber' | 'ignore';

interface ColumnDef {
  index: number;
  header: string;
  mappedTo: MappedTo;
}

interface ColumnMapping {
  columns: ColumnDef[];
  dateFormat: string;
  amountFormat: string;
}

const MAPPING_OPTIONS: { value: MappedTo; label: string }[] = [
  { value: 'date', label: 'Date' },
  { value: 'particulars', label: 'Description / Particulars' },
  { value: 'debit', label: 'Debit' },
  { value: 'credit', label: 'Credit' },
  { value: 'balance', label: 'Balance' },
  { value: 'chequeRefNumber', label: 'Ref No. / Cheque No.' },
  { value: 'ignore', label: 'Ignore (skip)' },
];

function guessMapping(header: string): MappedTo {
  const h = header.toLowerCase();
  if (/txn\s*date|transaction\s*date|^date$/i.test(h)) return 'date';
  if (/value\s*date/i.test(h)) return 'ignore';
  if (/desc|particular|narration|remark/i.test(h)) return 'particulars';
  if (/debit|withdrawal/i.test(h)) return 'debit';
  if (/credit|deposit/i.test(h)) return 'credit';
  if (/balance/i.test(h)) return 'balance';
  if (/ref|cheque|chq|instrument/i.test(h)) return 'chequeRefNumber';
  if (/branch|code|sr\s*no|s\.?\s*no/i.test(h)) return 'ignore';
  return 'ignore';
}

const AdminParserBuilder: React.FC = () => {
  const navigate = useNavigate();
  const { id: editId } = useParams<{ id: string }>();
  const isEditing = !!editId;

  const [step, setStep] = useState<'list' | 'upload' | 'map' | 'test' | 'save'>(isEditing ? 'upload' : 'list');
  const [parsers, setParsers] = useState<any[]>([]);
  const [loadingList, setLoadingList] = useState(false);

  const [uploading, setUploading] = useState(false);
  const [rawText, setRawText] = useState('');
  const [, setDetectedHeaders] = useState<string[]>([]);
  const [sampleRows, setSampleRows] = useState<{ raw: string; cells: string[] }[]>([]);
  const [sampleFileName, setSampleFileName] = useState('');

  const [columns, setColumns] = useState<ColumnDef[]>([]);
  const [dateFormat, setDateFormat] = useState('DD/MM/YYYY');

  const [generatedCode, setGeneratedCode] = useState('');
  const [editableCode, setEditableCode] = useState('');
  const [testResult, setTestResult] = useState<any>(null);
  const [testing, setTesting] = useState(false);
  const [testError, setTestError] = useState('');

  const [parserName, setParserName] = useState('');
  const [parserDescription, setParserDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadParsers = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await getStatementParsers();
      if (res.success) setParsers(res.data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    if (step === 'list') loadParsers();
  }, [step, loadParsers]);

  useEffect(() => {
    if (isEditing && editId) {
      (async () => {
        try {
          const res = await getStatementParser(parseInt(editId, 10));
          if (res.success && res.data) {
            const p = res.data;
            setParserName(p.name);
            setParserDescription(p.description || '');
            setEditableCode(p.parser_code);
            setGeneratedCode(p.parser_code);
            if (p.sample_raw_text) setRawText(p.sample_raw_text);
            if (p.sample_file_name) setSampleFileName(p.sample_file_name);
            if (p.column_mapping?.columns) {
              setColumns(p.column_mapping.columns);
              setDetectedHeaders(p.column_mapping.columns.map((c: ColumnDef) => c.header));
            }
            setStep('test');
          }
        } catch (e: any) {
          setError(e.message);
        }
      })();
    }
  }, [isEditing, editId]);

  const handleUpload = async (file: File) => {
    setUploading(true);
    setError('');
    try {
      const res = await extractColumnsFromPdf(file);
      if (res.success) {
        setRawText(res.data.rawText);
        setDetectedHeaders(res.data.detectedHeaders);
        setSampleRows(res.data.sampleRows);
        setSampleFileName(file.name);

        const cols: ColumnDef[] = res.data.detectedHeaders.map((h, i) => ({
          index: i,
          header: h,
          mappedTo: guessMapping(h),
        }));
        setColumns(cols);
        setStep('map');
      }
    } catch (e: any) {
      setError(e.message || 'Failed to extract columns');
    } finally {
      setUploading(false);
    }
  };

  const handleColumnChange = (index: number, field: 'header' | 'mappedTo', value: string) => {
    setColumns(prev => prev.map(c =>
      c.index === index ? { ...c, [field]: value } : c
    ));
  };

  const addColumn = () => {
    setColumns(prev => [...prev, { index: prev.length, header: '', mappedTo: 'ignore' }]);
  };

  const removeColumn = (index: number) => {
    setColumns(prev => prev.filter(c => c.index !== index).map((c, i) => ({ ...c, index: i })));
  };

  const handleGenerateAndTest = async () => {
    setTesting(true);
    setTestError('');
    setTestResult(null);
    try {
      const mapping: ColumnMapping = {
        columns,
        dateFormat,
        amountFormat: 'indian',
      };
      const res = await generateAndTestParser(mapping, rawText);
      if (res.success) {
        setGeneratedCode(res.data.generatedCode);
        setEditableCode(res.data.generatedCode);
        setTestResult(res.data.testResult);
        setStep('test');
      }
    } catch (e: any) {
      setTestError(e.message || 'Failed to generate/test parser');
    } finally {
      setTesting(false);
    }
  };

  const handleTestEditedCode = async () => {
    setTesting(true);
    setTestError('');
    setTestResult(null);
    try {
      const res = await testParserCode(editableCode, rawText);
      if (res.success) {
        setTestResult(res.data.testResult);
      }
    } catch (e: any) {
      setTestError(e.message || 'Failed to test parser');
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!parserName.trim()) { setError('Parser name is required'); return; }
    setSaving(true);
    setError('');
    try {
      if (isEditing && editId) {
        await updateStatementParser(parseInt(editId, 10), {
          name: parserName,
          description: parserDescription,
          columnMapping: { columns, dateFormat, amountFormat: 'indian' },
          parserCode: editableCode,
          sampleFileName,
          sampleRawText: rawText,
          status: 'approved',
        });
      } else {
        await createStatementParser({
          name: parserName,
          description: parserDescription,
          columnMapping: { columns, dateFormat, amountFormat: 'indian' },
          parserCode: editableCode,
          sampleFileName,
          sampleRawText: rawText,
          status: 'approved',
        });
      }
      navigate('/admin/parser-builder');
      setStep('list');
      resetState();
    } catch (e: any) {
      setError(e.message || 'Failed to save parser');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this parser?')) return;
    try {
      await deleteStatementParser(id);
      loadParsers();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const resetState = () => {
    setRawText('');
    setDetectedHeaders([]);
    setSampleRows([]);
    setSampleFileName('');
    setColumns([]);
    setGeneratedCode('');
    setEditableCode('');
    setTestResult(null);
    setTestError('');
    setParserName('');
    setParserDescription('');
    setError('');
  };

  const startNew = () => {
    resetState();
    setStep('upload');
  };

  // ─── LIST VIEW ───
  if (step === 'list') {
    return (
      <div className="parser-builder">
        <div className="parser-builder-header">
          <h1>Statement Parsers</h1>
          <button className="btn-primary" onClick={startNew}>+ Create New Parser</button>
        </div>
        {error && <div className="parser-error">{error}</div>}
        {loadingList ? (
          <div className="parser-loading">Loading parsers...</div>
        ) : parsers.length === 0 ? (
          <div className="parser-empty">No parsers created yet. Click "Create New Parser" to get started.</div>
        ) : (
          <div className="parser-list">
            <table className="parser-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {parsers.map(p => (
                  <tr key={p.id}>
                    <td>{p.id}</td>
                    <td className="parser-name-cell">{p.name}</td>
                    <td>{p.description || '—'}</td>
                    <td><span className={`status-badge status-${p.status}`}>{p.status}</span></td>
                    <td>{new Date(p.created_at).toLocaleDateString()}</td>
                    <td className="parser-actions">
                      <button className="btn-sm btn-outline" onClick={() => navigate(`/admin/parser-builder/${p.id}`)}>Edit</button>
                      <button className="btn-sm btn-danger" onClick={() => handleDelete(p.id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // ─── UPLOAD STEP ───
  if (step === 'upload') {
    return (
      <div className="parser-builder">
        <div className="parser-builder-header">
          <button className="btn-back" onClick={() => { resetState(); setStep('list'); navigate('/admin/parser-builder'); }}>← Back to Parsers</button>
          <h1>{isEditing ? 'Edit Parser' : 'Create New Parser'}</h1>
        </div>
        <div className="step-indicator">
          <span className="step active">1. Upload Sample</span>
          <span className="step">2. Map Columns</span>
          <span className="step">3. Test & Edit Code</span>
          <span className="step">4. Save</span>
        </div>
        {error && <div className="parser-error">{error}</div>}
        <div className="upload-zone">
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            style={{ display: 'none' }}
            onChange={e => {
              const f = e.target.files?.[0];
              if (f) handleUpload(f);
            }}
          />
          <div
            className="upload-dropzone"
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? (
              <p>Extracting columns from PDF...</p>
            ) : (
              <>
                <p className="upload-icon">📄</p>
                <p>Click to upload a sample bank statement PDF</p>
                <p className="upload-hint">The system will detect columns and show sample data</p>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ─── MAP COLUMNS STEP ───
  if (step === 'map') {
    return (
      <div className="parser-builder">
        <div className="parser-builder-header">
          <button className="btn-back" onClick={() => setStep('upload')}>← Back</button>
          <h1>Map Columns</h1>
        </div>
        <div className="step-indicator">
          <span className="step done">1. Upload</span>
          <span className="step active">2. Map Columns</span>
          <span className="step">3. Test & Edit Code</span>
          <span className="step">4. Save</span>
        </div>
        {error && <div className="parser-error">{error}</div>}

        <p className="step-desc">Map each detected column to the correct field. Mark columns like "Branch Code" or "Value Date" as <strong>Ignore</strong> so they don't interfere with parsing.</p>

        <div className="column-mapper">
          <table className="mapper-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Column Header</th>
                <th>Map To</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {columns.map((col, idx) => (
                <tr key={idx} className={col.mappedTo === 'ignore' ? 'row-ignored' : ''}>
                  <td className="col-index">{idx + 1}</td>
                  <td>
                    <input
                      type="text"
                      className="col-header-input"
                      value={col.header}
                      onChange={e => handleColumnChange(col.index, 'header', e.target.value)}
                      placeholder="Column name"
                    />
                  </td>
                  <td>
                    <select
                      className="col-mapping-select"
                      value={col.mappedTo}
                      onChange={e => handleColumnChange(col.index, 'mappedTo', e.target.value)}
                    >
                      {MAPPING_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <button className="btn-sm btn-danger" onClick={() => removeColumn(col.index)} title="Remove column">×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button className="btn-sm btn-outline" onClick={addColumn} style={{ marginTop: '8px' }}>+ Add Column</button>
        </div>

        {sampleRows.length > 0 && (
          <div className="sample-preview">
            <h3>Sample Data Preview</h3>
            <div className="sample-table-wrap">
              <table className="sample-table">
                <thead>
                  <tr>
                    {columns.map((col, i) => (
                      <th key={i} className={col.mappedTo === 'ignore' ? 'col-ignored' : ''}>{col.header || `Col ${i + 1}`}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sampleRows.map((row, ri) => (
                    <tr key={ri}>
                      {columns.map((col, ci) => (
                        <td key={ci} className={col.mappedTo === 'ignore' ? 'col-ignored' : ''}>{row.cells[ci] || ''}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="step-actions">
          <div className="date-format-row">
            <label>Date Format: </label>
            <select value={dateFormat} onChange={e => setDateFormat(e.target.value)}>
              <option value="DD/MM/YYYY">DD/MM/YYYY</option>
              <option value="MM/DD/YYYY">MM/DD/YYYY</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD</option>
              <option value="DD-MM-YYYY">DD-MM-YYYY</option>
            </select>
          </div>
          <button className="btn-primary" onClick={handleGenerateAndTest} disabled={testing}>
            {testing ? 'Generating & Testing...' : 'Generate & Test Parser →'}
          </button>
        </div>
        {testError && <div className="parser-error">{testError}</div>}
      </div>
    );
  }

  // ─── TEST & CODE STEP ───
  if (step === 'test') {
    return (
      <div className="parser-builder">
        <div className="parser-builder-header">
          <button className="btn-back" onClick={() => setStep(columns.length > 0 ? 'map' : 'upload')}>← Back</button>
          <h1>Test & Edit Parser Code</h1>
        </div>
        <div className="step-indicator">
          <span className="step done">1. Upload</span>
          <span className="step done">2. Map Columns</span>
          <span className="step active">3. Test & Edit Code</span>
          <span className="step">4. Save</span>
        </div>

        {error && <div className="parser-error">{error}</div>}

        <div className="code-editor-section">
          <div className="code-header">
            <h3>Parser Code</h3>
            <span className="code-hint">You can edit this code directly. Click "Re-Test" to verify your changes.</span>
          </div>
          <textarea
            className="code-editor"
            value={editableCode}
            onChange={e => setEditableCode(e.target.value)}
            spellCheck={false}
          />
          <div className="code-actions">
            <button className="btn-outline" onClick={() => setEditableCode(generatedCode)} disabled={editableCode === generatedCode}>
              Reset to Generated
            </button>
            <button className="btn-primary" onClick={handleTestEditedCode} disabled={testing}>
              {testing ? 'Testing...' : 'Re-Test Parser'}
            </button>
          </div>
        </div>

        {testError && <div className="parser-error">{testError}</div>}

        {testResult && (
          <div className="test-results">
            <div className="test-summary">
              <h3>Test Results</h3>
              <div className="summary-stats">
                <span className="stat"><strong>{testResult.transactionCount}</strong> transactions</span>
                <span className="stat"><strong>{testResult.creditCount}</strong> credits</span>
                <span className="stat">Total Credits: <strong>₹{testResult.totalCredits?.toLocaleString('en-IN')}</strong></span>
                <span className="stat">Total Debits: <strong>₹{testResult.totalDebits?.toLocaleString('en-IN')}</strong></span>
              </div>
            </div>
            <div className="test-table-wrap">
              <table className="test-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Particulars</th>
                    <th>Debit</th>
                    <th>Credit</th>
                    <th>Balance</th>
                    <th>Ref Numbers</th>
                  </tr>
                </thead>
                <tbody>
                  {testResult.transactions?.slice(0, 20).map((txn: any, i: number) => (
                    <tr key={i}>
                      <td className="col-date">{txn.date}</td>
                      <td className="col-desc">{txn.particulars}</td>
                      <td className="col-amount">{txn.debit != null ? `₹${txn.debit.toLocaleString('en-IN')}` : '—'}</td>
                      <td className="col-amount col-credit">{txn.credit != null ? `₹${txn.credit.toLocaleString('en-IN')}` : '—'}</td>
                      <td className="col-amount">{txn.balance != null ? `₹${txn.balance.toLocaleString('en-IN')}` : '—'}</td>
                      <td className="col-refs">{txn.transactionRefs?.join(', ') || txn.chequeRefNumber || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {testResult.transactions?.length > 20 && (
                <p className="more-rows">...and {testResult.transactions.length - 20} more transactions</p>
              )}
            </div>
          </div>
        )}

        <div className="step-actions">
          <button className="btn-primary" onClick={() => setStep('save')} disabled={!testResult}>
            Looks Good → Name & Save
          </button>
        </div>

        <div className="reupload-section">
          <h4>Re-test with a different PDF</h4>
          <input
            type="file"
            accept="application/pdf"
            onChange={async e => {
              const f = e.target.files?.[0];
              if (!f) return;
              setUploading(true);
              setTestError('');
              try {
                const res = await extractColumnsFromPdf(f);
                if (res.success) {
                  setRawText(res.data.rawText);
                  setSampleFileName(f.name);
                  setSampleRows(res.data.sampleRows);
                  const testRes = await testParserCode(editableCode, res.data.rawText);
                  if (testRes.success) setTestResult(testRes.data.testResult);
                }
              } catch (err: any) {
                setTestError(err.message);
              } finally {
                setUploading(false);
              }
            }}
          />
        </div>
      </div>
    );
  }

  // ─── SAVE STEP ───
  return (
    <div className="parser-builder">
      <div className="parser-builder-header">
        <button className="btn-back" onClick={() => setStep('test')}>← Back</button>
        <h1>{isEditing ? 'Update Parser' : 'Save Parser'}</h1>
      </div>
      <div className="step-indicator">
        <span className="step done">1. Upload</span>
        <span className="step done">2. Map Columns</span>
        <span className="step done">3. Test</span>
        <span className="step active">4. Save</span>
      </div>

      {error && <div className="parser-error">{error}</div>}

      <div className="save-form">
        <div className="form-group">
          <label htmlFor="parser-name">Parser Name *</label>
          <input
            id="parser-name"
            type="text"
            className="form-input"
            value={parserName}
            onChange={e => setParserName(e.target.value)}
            placeholder='e.g. "HDFC Branch Code Format"'
          />
        </div>
        <div className="form-group">
          <label htmlFor="parser-desc">Description</label>
          <textarea
            id="parser-desc"
            className="form-textarea"
            value={parserDescription}
            onChange={e => setParserDescription(e.target.value)}
            placeholder="Optional notes about which bank/format this parser handles"
            rows={3}
          />
        </div>

        {testResult && (
          <div className="save-preview">
            <p><strong>{testResult.transactionCount}</strong> transactions parsed from sample &middot; <strong>{testResult.creditCount}</strong> credits &middot; File: {sampleFileName}</p>
          </div>
        )}

        <div className="step-actions">
          <button className="btn-primary btn-lg" onClick={handleSave} disabled={saving || !parserName.trim()}>
            {saving ? 'Saving...' : isEditing ? 'Update Parser' : 'Approve & Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminParserBuilder;
